/**
 * UserPicker — F12.C T12.C.2 + T3 (company scoping, ADR wave „pola dodatkowe
 * na listach i picker").
 *
 * Dropdown z wyszukiwarką po użytkownikach tenanta.
 * Zastępuje stub UUID input w UserFieldType.
 *
 * Pobiera listę użytkowników z GET /api/users?limit=100(&companyId=). Serwer
 * (`crm_core.users.view`, `resolveListCompanyScope`) wspiera już `?companyId=`
 * (Model B, CLAUDE.md §4.8: user widoczny gdy ma dostęp do TEJ firmy;
 * super-user/scope='all' bez zawężenia widzi wszystkich) — `companyId` tutaj
 * jest czystym przekazaniem query param, brak logiki widoczności po stronie
 * klienta.
 *
 * Wyszukiwanie: GET /api/users wspiera serwerowy `?search=` (ILIKE po
 * email/display_name, patrz `apps/api/app/api/users/route.ts`) — użyty tu z
 * debounce (300ms) zamiast filtrowania klient-side, żeby skalować się ponad
 * limit=100 userów w tenancie i pozostać w parytecie ze scopingiem firmowym
 * (serwer filtruje search PO zawężeniu do firmy, klient nigdy nie zobaczy
 * dopasowań spoza zakresu). Lista BAZOWA (bez `search`) jest fetch'owana
 * osobno i używana do rozwiązania etykiety AKTUALNIE wybranego usera w
 * zamkniętym triggerze — inaczej wpisanie frazy w dropdownie zwężałoby wynik
 * i etykieta wybranego usera migałaby na placeholder do czasu wyczyszczenia
 * pola wyszukiwania.
 *
 * Etykieta wybranego usera NIEZALEŻNA od listy bazowej (fix po code review
 * T3): lista bazowa niesie tylko do 100 userów — gdy `value` wskazuje kogoś
 * POZA nią, `selectedUser` byłby `undefined` i trigger cichnąłby na
 * placeholder (wygląda jak „nic nie wybrano", mimo że wartość jest ustawiona).
 * Fallback: (1) `selectedLabel` prop — gdy wołający (np. `RelationPicker`
 * w delegacji do usera, patrz jego nagłówek) JUŻ rozwiązał etykietę własną
 * ścieżką (adapter `detailPath`), przekazuje ją tu i unikamy duplikującego
 * fetcha; (2) brak `selectedLabel` → osobny `GET /users/:id` (ten sam
 * endpoint co szczegół kartoteki), włączany TYLKO gdy `value` nie jest w
 * liście bazowej i `selectedLabel` nie podano. Gdy oba zawiodą (404/403) —
 * skrócone id (mirror `ContractorSearchPicker`), nie generyczny placeholder
 * (żeby nie wygladało na „brak wyboru").
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Loader2, Search, User, X } from 'lucide-react';

import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { api } from '@/lib/api';

export interface TenantUser {
  id: string;
  displayName: string | null;
  email: string;
}

interface UsersResponse {
  users: TenantUser[];
}

interface UserPickerProps {
  value: string | null;
  /** `row` = wybrany user (gdy dostępny w bieżącej liście) — opcjonalny, backward-compatible. */
  onChange: (value: string | null, row?: TenantUser) => void;
  /**
   * Firma dla scopingu listy (`?companyId=` na GET /api/users) — `undefined`/`null`
   * = picker nieograniczony (brak kontekstu firmy w formularzu, boundary rule
   * CLAUDE.md §4.8: nigdy nie blokuj pickera przy braku firmy).
   */
  companyId?: string | null;
  /**
   * Etykieta wybranego usera, GDY wołający już ją rozwiązał inną ścieżką
   * (patrz nagłówek pliku — np. `RelationPicker`). Gdy podana, wygasza
   * wewnętrzny fallback `GET /users/:id` (brak duplikującego fetcha).
   */
  selectedLabel?: string | null;
  /**
   * `true` gdy wołający JESZCZE rozwiązuje `selectedLabel` własną ścieżką
   * (np. `RelationPicker`'s `selectedFetched` w toku) — wygasza fallback
   * `GET /users/:id` NA CZAS trwania tego fetcha, żeby nie strzelić
   * duplikującym zapytaniem tylko dlatego, że `selectedLabel` jest chwilowo
   * `null` (jeszcze nierozwiązana, nie "nieobecna").
   */
  selectedLabelPending?: boolean;
  placeholder?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

const SEARCH_DEBOUNCE_MS = 300;

function userLabel(u: TenantUser): string {
  return u.displayName?.trim() ? u.displayName : u.email;
}

function buildUsersUrl(companyId?: string | null, search?: string): string {
  const params = new URLSearchParams({ limit: '100' });
  if (companyId) params.set('companyId', companyId);
  if (search && search.trim()) params.set('search', search.trim());
  return `/users?${params.toString()}`;
}

export function UserPicker({
  value,
  onChange,
  companyId = null,
  selectedLabel = null,
  selectedLabelPending = false,
  placeholder = 'Wybierz użytkownika…',
  disabled = false,
  'data-testid': testId,
}: UserPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);

  // Lista bazowa (bez `search`) — rozwiązuje etykietę wybranego usera w
  // zamkniętym triggerze (patrz nagłówek pliku) i jest widokiem startowym
  // dropdownu przed wpisaniem frazy.
  const {
    data: baseData,
    isLoading: baseLoading,
    isFetched: baseIsFetched,
  } = useQuery<UsersResponse>({
    queryKey: ['users-for-picker', companyId ?? null],
    queryFn: async () => {
      const res = await api.get<UsersResponse>(buildUsersUrl(companyId));
      return res.data;
    },
    staleTime: 120_000,
    enabled: isOpen || !!value,
  });

  const hasSearch = debouncedSearch.trim().length > 0;

  // Wyszukiwanie serwerowe (debounced) — osobny query, żeby nie tracić listy
  // bazowej (etykieta wybranego usera) podczas wpisywania frazy.
  const { data: searchData, isLoading: searchLoading } = useQuery<UsersResponse>({
    queryKey: ['users-for-picker', companyId ?? null, debouncedSearch],
    queryFn: async () => {
      const res = await api.get<UsersResponse>(buildUsersUrl(companyId, debouncedSearch));
      return res.data;
    },
    staleTime: 30_000,
    enabled: isOpen && hasSearch,
  });

  const baseUsers: TenantUser[] = baseData?.users ?? [];
  const displayedUsers: TenantUser[] = hasSearch ? (searchData?.users ?? []) : baseUsers;
  const isLoading = hasSearch ? searchLoading : baseLoading;

  const selectedUserFromBase = baseUsers.find((u) => u.id === value);
  const isValueResolvedLocally = !!selectedUserFromBase;

  // Fallback: `value` wskazuje kogoś POZA listą bazową (>100 userów w tenancie) —
  // dociągnij szczegół. Wygaszone gdy: (a) lista bazowa jeszcze NIE osiadła
  // (`baseIsFetched`) — inaczej strzelałby przed jej rozstrzygnięciem, nawet dla
  // wartości które są w tych 100 (baseUsers=[] podczas pierwszego fetcha sprawiało,
  // że `isValueResolvedLocally` chwilowo=false); (b) `selectedLabel` już rozwiązuje
  // etykietę (wołający ma własną ścieżkę, np. RelationPicker); (c) `selectedLabelPending`
  // — ta ścieżka JESZCZE trwa, `selectedLabel` jest chwilowo `null`, nie „nieobecna".
  const { data: detailData } = useQuery<{ user: TenantUser }>({
    queryKey: ['users-for-picker-detail', value],
    queryFn: async () => {
      const res = await api.get<{ user: TenantUser }>(`/users/${value}`);
      return res.data;
    },
    staleTime: 120_000,
    enabled:
      !!value &&
      baseIsFetched &&
      !isValueResolvedLocally &&
      selectedLabel == null &&
      !selectedLabelPending,
  });

  const selectedUser =
    selectedUserFromBase ?? (detailData?.user.id === value ? detailData.user : undefined);

  // Etykieta zamkniętego triggera: selectedUser (bazowa lista albo fallback detail)
  // → selectedLabel (rozwiązana przez wołającego) → skrócone id (mirror
  // ContractorSearchPicker) — NIGDY placeholder, gdy `value` jest ustawiony (patrz
  // nagłówek pliku — placeholder sugerowałby "brak wyboru").
  const hasValue = !!value;
  const displayLabel = selectedUser
    ? userLabel(selectedUser)
    : selectedLabel?.trim() || (value ? value.slice(0, 8) + '…' : '');

  function open() {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => searchRef.current?.focus(), 50);
  }

  return (
    <div className="relative" data-testid={testId}>
      {/* Trigger */}
      <div
        role="combobox"
        aria-expanded={isOpen}
        tabIndex={disabled ? -1 : 0}
        onClick={open}
        onKeyDown={(e) => e.key === 'Enter' && open()}
        className={[
          'flex min-h-[38px] w-full cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition',
          'bg-bg-primary',
          disabled
            ? 'cursor-not-allowed opacity-50 border-border-primary text-text-muted'
            : 'border-border-primary text-text-primary hover:border-accent-400 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500',
        ].join(' ')}
      >
        <User className="h-4 w-4 flex-shrink-0 text-text-muted" />
        {hasValue && displayLabel ? (
          <span className="flex-1 truncate">{displayLabel}</span>
        ) : (
          <span className="flex-1 text-text-muted">{placeholder}</span>
        )}
        {hasValue && !disabled && (
          <IconButton
            aria-label={t('common.searchableSelect.clearAriaLabel')}
            title={t('common.searchableSelect.clearAriaLabel')}
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="flex-shrink-0 h-auto w-auto p-0.5 hover:text-danger-600"
            tabIndex={-1}
          >
            <X className="h-3.5 w-3.5" />
          </IconButton>
        )}
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setIsOpen(false);
              setSearch('');
            }}
          />
          <div className="absolute z-50 mt-1 w-full rounded-lg border border-border-primary bg-bg-primary shadow-lg">
            {/* Search */}
            <div className="p-2 border-b border-border-primary">
              <Input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Szukaj po nazwie lub email…"
                prefixIcon={<Search className="h-3.5 w-3.5" />}
                containerClassName="gap-0"
                className="py-1.5 text-sm"
                data-testid={testId ? `${testId}-search` : undefined}
              />
            </div>

            {/* Users */}
            <div className="max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
                </div>
              ) : displayedUsers.length === 0 ? (
                <p className="p-3 text-center text-sm text-text-muted">
                  {search ? 'Brak wyników' : 'Brak użytkowników'}
                </p>
              ) : (
                displayedUsers.map((u) => (
                  <div
                    key={u.id}
                    onClick={() => {
                      onChange(u.id, u);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={[
                      'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition',
                      value === u.id
                        ? 'bg-accent-50 text-accent-700'
                        : 'text-text-primary hover:bg-bg-secondary',
                    ].join(' ')}
                    data-testid={testId ? `${testId}-option-${u.id}` : undefined}
                  >
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-accent-100 text-xs font-semibold text-accent-700">
                      {(u.displayName ?? u.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-medium">{userLabel(u)}</div>
                      {u.displayName && (
                        <div className="truncate text-xs text-text-muted">{u.email}</div>
                      )}
                    </div>
                    {value === u.id && (
                      <Check className="ml-auto h-4 w-4 flex-shrink-0 text-accent-600" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
